/* ------------------------------------------------------------------------------
 *
 *  # Calendario de Eventos - FullCalendar Integration
 *
 *  Gestión de calendario de eventos con FullCalendar, modal crear/editar evento
 *
 * ---------------------------------------------------------------------------- */

// Variables de estado
let calendarioEventosCallbacks = null;
let calendarInstance = null;
let currentView = 'dayGridMonth';

// Configuración de colores por defecto
const DEFAULT_EVENT_COLOR = '#3788d8';

// Helper functions
function getEventColor(evento) {
	return evento.color || evento.extendedProps?.tipoEventoColor || DEFAULT_EVENT_COLOR;
}

function getEventTitle(evento) {
	// No mostrar cupos si el evento no requiere inscripción
	if (evento.extendedProps?.requiereInscripcion === false) {
		return evento.title;
	}

	// Si tiene cupos disponibles
	if (evento.extendedProps?.cupoDisponible !== undefined) {
		const cupos = evento.extendedProps.cupoDisponible;
		
		// Si es un número muy grande (ilimitado) o null
		if (cupos > 999999 || cupos === null) {
			return `${evento.title} (Cupos ilimitados)`;
		}
		
		// Si hay cupos específicos
		if (cupos === 0) {
			return `${evento.title} (Completo)`;
		}
		
		return `${evento.title} (${cupos} cupos)`;
	}
	
	return evento.title;
}

function getEventClassNames(evento) {
	const classes = ['fc-event-calendario'];
	
	if (evento.allDay) {
		classes.push('fc-event-allday');
	}
	
	if (evento.extendedProps?.esPublico) {
		classes.push('fc-event-publico');
	}
	
	if (evento.extendedProps?.cupoDisponible === 0) {
		classes.push('fc-event-completo');
	}
	
	return classes;
}

// FullCalendar initialization
export function initCalendario(containerId, eventos = [], dotNetRef = null) {
	if (typeof FullCalendar == 'undefined') {
		console.error('FullCalendar library is not loaded.');
		return null;
	}

	const calendarEl = document.getElementById(containerId);
	if (!calendarEl) {
		console.warn(`Calendar container #${containerId} not found`);
		return null;
	}

	// Destroy previous instance
	if (calendarInstance) {
		calendarInstance.destroy();
		calendarInstance = null;
	}

	// Convertir eventos al formato de FullCalendar
	const calendarEvents = eventos.map(e => ({
		id: e.id.toString(),
		title: e.title,
		start: e.start,
		end: e.end,
		allDay: e.allDay,
		color: e.color || DEFAULT_EVENT_COLOR,
		extendedProps: e.extendedProps || {}
	}));

	console.log('calendarEvents', calendarEvents);

	try {
		calendarInstance = new FullCalendar.Calendar(calendarEl, {
			headerToolbar: {
				left: 'prev,next today',
				center: 'title',
				right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek'
			},
			initialView: currentView,
			locale: 'es',
			buttonText: {
				today: 'Hoy',
				month: 'Mes',
				week: 'Semana',
				day: 'Día',
				list: 'Lista'
			},
			navLinks: true,
			selectable: true,
			selectMirror: true,
			editable: false, // No drag & drop por ahora
			dayMaxEvents: true,
			weekNumbers: true,
			weekText: 'Sem',
			events: calendarEvents,

			// Event rendering
			eventContent: function (arg) {
				const title = getEventTitle(arg.event);
				const event = arg.event;

				// Construir el tooltip con información del evento
				const tooltipText = [
					event.title,
					event.extendedProps?.descripcion,
					event.extendedProps?.ubicacion,
					`Inicio: ${event.start?.toLocaleString()}`,
					event.end ? `Fin: ${event.end?.toLocaleString()}` : null
				].filter(Boolean).join('\n');

				return {
					html: `<div class="fc-event-main-frame" title="${tooltipText}">
							<div class="fc-event-time">${arg.timeText}</div>
							<div class="fc-event-title-container">
								<div class="fc-event-title fc-sticky">${title}</div>
							</div>
						   </div>`
				};
			},

			eventClassNames: function (arg) {
				return getEventClassNames(arg.event);
			},

			// Event click
			eventClick: function (info) {
				info.jsEvent.preventDefault();
				console.log('Event clicked:', info.event.id);

				if (dotNetRef) {
					dotNetRef.invokeMethodAsync('OnEventClick', parseInt(info.event.id));
				}
			},

			// Date select (crear nuevo evento)
			select: function (info) {
				console.log('Date selected:', info.startStr, 'to', info.endStr);

				if (dotNetRef) {
					dotNetRef.invokeMethodAsync('OnDateSelect',
						info.startStr,
						info.endStr,
						info.allDay
					);
				}

				// Deselect
				calendarInstance.unselect();
			},

			// View change
			datesSet: function (info) {
				currentView = info.view.type;
				console.log('View changed to:', currentView);

				// Recargar eventos del rango visible
				if (dotNetRef) {
					dotNetRef.invokeMethodAsync('OnViewChange',
						info.startStr,
						info.endStr,
						currentView
					);
				}
			},

			// Loading
			loading: function (isLoading) {
				if (isLoading) {
					window.sfBlazorSB.toggleSpinner(true);
				} else {
					window.sfBlazorSB.toggleSpinner(false);
				}
			}
		});
	} catch (e) {
		console.log('FullCalendar error', e);
	}
	

	calendarInstance.render();
	console.log('FullCalendar initialized with', eventos.length, 'events');
	
	return calendarInstance;
}

// Add event to calendar
export function addEventToCalendar(evento) {
	if (!calendarInstance) {
		console.warn('Calendar not initialized');
		return;
	}

	const calendarEvent = {
		id: evento.id.toString(),
		title: evento.title,
		start: evento.start,
		end: evento.end,
		allDay: evento.allDay,
		color: evento.color || DEFAULT_EVENT_COLOR,
		extendedProps: evento.extendedProps || {}
	};

	calendarInstance.addEvent(calendarEvent);
	console.log('Event added to calendar:', evento.id);
}

// Update event in calendar
export function updateEventInCalendar(evento) {
	if (!calendarInstance) {
		console.warn('Calendar not initialized');
		return;
	}

	const existingEvent = calendarInstance.getEventById(evento.id.toString());
	if (existingEvent) {
		existingEvent.setProp('title', evento.title);
		existingEvent.setStart(evento.start);
		existingEvent.setEnd(evento.end);
		existingEvent.setProp('allDay', evento.allDay);
		existingEvent.setProp('color', evento.color || DEFAULT_EVENT_COLOR);
		existingEvent.setExtendedProp('extendedProps', evento.extendedProps || {});
		
		console.log('Event updated in calendar:', evento.id);
	}
}

// Remove event from calendar
export function removeEventFromCalendar(eventoId) {
	if (!calendarInstance) {
		console.warn('Calendar not initialized');
		return;
	}

	const event = calendarInstance.getEventById(eventoId.toString());
	if (event) {
		event.remove();
		console.log('Event removed from calendar:', eventoId);
	}
}

// Refresh all events
export function refreshCalendarEvents(eventos) {
	if (!calendarInstance) {
		console.warn('Calendar not initialized');
		return;
	}

	// Remove all events
	calendarInstance.getEvents().forEach(event => event.remove());
	
	// Add new events
	eventos.forEach(e => {
		const calendarEvent = {
			id: e.id.toString(),
			title: e.title,
			start: e.start,
			end: e.end,
			allDay: e.allDay,
			color: e.color || DEFAULT_EVENT_COLOR,
			extendedProps: e.extendedProps || {}
		};
		calendarInstance.addEvent(calendarEvent);
	});
	
	console.log('Calendar refreshed with', eventos.length, 'events');
}

// Go to date
export function gotoDate(dateStr) {
	if (!calendarInstance) {
		console.warn('Calendar not initialized');
		return;
	}

	calendarInstance.gotoDate(dateStr);
	console.log('Calendar navigated to:', dateStr);
}

// Change view
export function changeView(viewType) {
	if (!calendarInstance) {
		console.warn('Calendar not initialized');
		return;
	}

	calendarInstance.changeView(viewType);
	currentView = viewType;
	console.log('Calendar view changed to:', viewType);
}

// Get current view info
export function getCurrentViewInfo() {
	if (!calendarInstance) {
		return null;
	}

	const view = calendarInstance.view;
	return {
		type: view.type,
		title: view.title,
		currentStart: view.currentStart.toISOString(),
		currentEnd: view.currentEnd.toISOString()
	};
}

// Destroy calendar
export function destroyCalendar() {
	if (calendarInstance) {
		calendarInstance.destroy();
		calendarInstance = null;
		console.log('Calendar destroyed');
	}
}

// Callbacks
export function setCalendarioEventosCallbacks(dotNetRef) {
	calendarioEventosCallbacks = dotNetRef;
}

// Modal functions
export function openCreateEventModal() {
	if (calendarioEventosCallbacks) {
		calendarioEventosCallbacks.invokeMethodAsync('OpenCreateModal');
	}
}

export function openEditEventModal(eventoId) {
	if (calendarioEventosCallbacks) {
		calendarioEventosCallbacks.invokeMethodAsync('OpenEditModal', eventoId);
	}
}

export function openEventDetailsModal(eventoId) {
	if (calendarioEventosCallbacks) {
		calendarioEventosCallbacks.invokeMethodAsync('OpenDetailsModal', eventoId);
	}
}

// Filters
export function applyCalendarioFilters() {
	const tipoEventoFilter = document.getElementById('tipoEventoFilter')?.value || '';
	const ambienteFilter = document.getElementById('ambienteFilter')?.value || '';
	const soloPublicos = document.getElementById('soloPublicosFilter')?.checked || false;
	const soloConCupo = document.getElementById('soloConCupoFilter')?.checked || false;
	
	if (calendarioEventosCallbacks) {
		calendarioEventosCallbacks.invokeMethodAsync('ApplyFilters', 
			tipoEventoFilter, 
			ambienteFilter, 
			soloPublicos, 
			soloConCupo
		);
	}
}

export function clearCalendarioFilters() {
	const tipoEventoFilter = document.getElementById('tipoEventoFilter');
	const ambienteFilter = document.getElementById('ambienteFilter');
	const soloPublicos = document.getElementById('soloPublicosFilter');
	const soloConCupo = document.getElementById('soloConCupoFilter');
	
	if (tipoEventoFilter) tipoEventoFilter.value = '';
	if (ambienteFilter) ambienteFilter.value = '';
	if (soloPublicos) soloPublicos.checked = false;
	if (soloConCupo) soloConCupo.checked = false;
	
	if (calendarioEventosCallbacks) {
		calendarioEventosCallbacks.invokeMethodAsync('ClearFilters');
	}
}

// Initialize date/time pickers for event form
export function initEventoDateTimePickers(inicioId, finId, dotNetRef = null) {
	if (!window.jQuery || !$.fn.daterangepicker) {
		console.warn('DateRangePicker not loaded');
		return;
	}

	// Date range picker para fechas
	$(`#${inicioId}, #${finId}`).daterangepicker({
		singleDatePicker: true,
		showDropdowns: true,
		minYear: new Date().getFullYear(),
		maxYear: parseInt(moment().format('YYYY'), 10) + 5,
		locale: {
			format: 'DD/MM/YYYY',
			applyLabel: 'Aplicar',
			cancelLabel: 'Cancelar',
			daysOfWeek: ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa'],
			monthNames: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 
						 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
			firstDay: 1
		}
	});

	// Callbacks
	if (dotNetRef) {
		$(`#${inicioId}`).on('apply.daterangepicker', function(ev, picker) {
			dotNetRef.invokeMethodAsync('OnFechaInicioChanged', picker.startDate.format('YYYY-MM-DD'));
		});

		$(`#${finId}`).on('apply.daterangepicker', function(ev, picker) {
			dotNetRef.invokeMethodAsync('OnFechaFinChanged', picker.startDate.format('YYYY-MM-DD'));
		});
	}
}

// Color picker initialization
export function initColorPicker(elementId, initialColor = DEFAULT_EVENT_COLOR, dotNetRef = null) {
	const $input = $(`#${elementId}`);
	if (!$input.length) {
		console.warn('Color picker element not found:', elementId);
		return;
	}

	// Simple HTML5 color input
	$input.attr('type', 'color');
	$input.val(initialColor);

	if (dotNetRef) {
		$input.on('change', function() {
			dotNetRef.invokeMethodAsync('OnColorChanged', $(this).val());
		});
	}
}
